import { InitProperties } from '../models/init-properties';
import { LogChannel, LoggingMessageInterface } from '@rx-applications/shared/logging';
import { inject, Injectable } from '@angular/core';
import { UserProperties } from '../models/intercom-message.model';
import { ClientsiteBaseHandler } from './clientsite-base.handler';
import { validateTrue } from '@rx-applications/shared/utils';
import { LoggingPropertyConfiguration } from '../models/logging-property-configuration';
import { NoopIntercom } from './noop.handlers';
import { Intercom } from '@supy-io/ngx-intercom';
import { FeatureFlagEnum } from '@rx-applications/shared/clientsite/feature-flags';

@Injectable({ providedIn: 'root' })
export class IntercomHandler extends ClientsiteBaseHandler {
  protected channelToSubscribe: LogChannel = LogChannel.Intercom;

  private get intercomInstance() {
    return this.enabled ? this.#intercom : new NoopIntercom();
  }

  #intercom = inject(Intercom);

  init(initProperties: InitProperties) {
    const tracker = initProperties?.environmentTracking?.['intercom'];
    const features = initProperties?.baseConfig?.features;

    if (!tracker || !features) {
      return;
    }

    if (this.subscription) {
      return; // already initialized
    }

    if (features[FeatureFlagEnum.DisableIntercom]) {
      return;
    }

    this.enabled = validateTrue(tracker.enabled);
    this.createSubscription();
    this.#startSession(tracker.key);
  }

  createSetup(properties: LoggingPropertyConfiguration): void {
    if (properties.userProperties) {
      this.#update(properties.userProperties);
    }
    if (properties.endSession) {
      this.#endSession();
    }
  }

  update(properties: UserProperties) {
    this.intercomInstance.update(properties);
  }

  protected override messageHandler = (value: LoggingMessageInterface) => {
    if (!value.props) {
      value.props = {};
    }
    this.intercomInstance.trackEvent(value.name, value.props);
  };

  #update(properties: UserProperties) {
    this.intercomInstance.update(properties);
  }

  #startSession(app_id: string) {
    this.intercomInstance.boot({ app_id });
  }

  #endSession() {
    this.intercomInstance.shutdown();
  }
}
